import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { type DataTypeType, getLeadAttributes, type GetLeadAttributesType } from '@repo/advanced-filters';
import {
  filterMailMergeOptions, FORM_FIELD_SUPPORTED_ENTITIES, FormFieldItem, getMailMergeValue, isMailMergeUsed,
} from '@repo/crm-atoms';
import { useGetGraphqlDataQuery, type UseQueryResult } from '@repo/services';
import { message } from '@repo/ui';
import { translate, TranslationEntity } from '@repo/utils';
import { type NodeProps } from '@xyflow/react';
import {
  AutoComplete,
  Button, Card, Checkbox, Flex, Form, Input, List, Select,
} from 'antd';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import useMailMergeFields from 'automations/hooks/useMailMergeFields';
import { getParentIdSelector } from 'automations/selectors/reactFlow';
import { addAdditionalOptions } from 'automations/store/reactFlowSlice';
import { type UpdateLeadNodeData, type UpdateLeadNodeType } from 'automations/types/nodeSchemas/actionNodes';
import { nodeSchemas } from 'automations/types/nodeTypes';
import { saveActionNode } from 'automations/utils/actionNode';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { ZodError } from 'zod';

import styles from './UpdateLeadActionNodeContent.module.scss';

type LeadField = UpdateLeadNodeData['additionalOptions']['updateLead'][number];

const initialValue: Partial<LeadField> = {
  schemaName: undefined,
  value: '',
};

function SelectSchemaName({
  onChange,
  value,
  options,
  loading,
}: {
  onChange?: (value: string) => void;
  value?: string;
  options: { value: string; label: string; dataType: DataTypeType; schemaName: string; }[];
  loading: boolean;
}) {
  const parsedOptions = useMemo(() => options?.map(({ label, schemaName }) => ({
    label,
    value: schemaName,
  })), [options]);

  useEffect(() => {
    if (parsedOptions?.length && !value) {
      onChange?.(parsedOptions[0]?.value ?? '');
    }
  }, [parsedOptions, value, onChange]);

  return (
    <Select
      loading={loading}
      className={styles.select}
      placeholder={translate(
        `Select #{${TranslationEntity.lead}} Field`,
        [{ capitalize: true, withArticle: true }],
      )}
      options={parsedOptions}
      showSearch
      optionFilterProp="label"
      value={value}
      onChange={onChange}
    />
  );
}

function RenderValueInput({
  value,
  onChange,
  selectedSchemaName,
  options,
}: {
  value: string;
  onChange?: (value: string) => void;
  selectedSchemaName?: string;
  options: GetLeadAttributesType['leadAttributes'];
}) {
  const [useMailMerge, setUseMailMerge] = useState(isMailMergeUsed(value));
  const selectedOption = options.find((option) => option.schemaName === selectedSchemaName);

  const {
    options: mailMergeOptions,
  } = useMailMergeFields({
    allowedDataTypes: [selectedOption?.dataType ?? ''],
  });

  const showOptions = value.includes('@');
  const searchString = value.slice(value.lastIndexOf('@') + 1);
  const filteredOptions = filterMailMergeOptions(mailMergeOptions, searchString);

  const handleSelect = useCallback((newValue: string) => {
    onChange?.(value.slice(0, value.lastIndexOf('@')) + getMailMergeValue(newValue));
  }, [onChange, value]);

  const handleFormFieldItemChange = useCallback((_: any, allFields: any) => {
    if (value !== allFields[selectedOption?.schemaName ?? '']) {
      onChange?.(allFields[selectedOption?.schemaName ?? '']);
    }
  }, [value, selectedOption?.schemaName, onChange]);

  return (
    <Flex gap={24}>
      {useMailMerge ? (
        <AutoComplete
          options={showOptions ? filteredOptions : []}
          value={value}
          onChange={(val) => onChange?.(val)}
          className={styles.valueInput}
          onSelect={handleSelect}
        >
          <Input placeholder="Type @ to add a mail merge field" />
        </AutoComplete>
      ) : (
        <Form onValuesChange={handleFormFieldItemChange}>
          <FormFieldItem
            mode="edit"
            displayName=""
            formItemProps={{
              noStyle: true,
            }}
            value={value}
            dataType={selectedOption?.dataType ?? ''}
            placeholder={selectedOption?.label ?? ''}
            schemaName={selectedOption?.schemaName ?? ''}
            dataTypeConfig={selectedOption?.dataTypeConfig}
            entityId={Number(selectedOption?.value)}
            entityName={FORM_FIELD_SUPPORTED_ENTITIES.leadAttribute}
            dropdownInputProps={{
              className: styles.valueInput,
              showSearch: true,
              optionFilterProp: 'label',
            }}
            ownerInputProps={{
              className: styles.valueInput,
            }}
          />
        </Form>
      )}
      <Checkbox
        checked={useMailMerge}
        onChange={() => setUseMailMerge((prev) => !prev)}
        className={styles.checkbox}
      >
        Use Mail Merge
      </Checkbox>
    </Flex>
  );
}

export default function AddLeadFields({
  node,
}: {
  node: NodeProps<UpdateLeadNodeType>;
}) {
  const { setMenuOpen, contentMode } = useCommonNodeHandles(node);
  const dispatch = useAutomationDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [leadFields, setLeadFields] = useState<Partial<LeadField>[]>(
    node.data?.additionalOptions?.updateLead ?? [initialValue],
  );
  const parentNodeId = useSelector(getParentIdSelector(node.id));

  const {
    data,
    isLoading: attributesLoading,
  } = useGetGraphqlDataQuery<UseQueryResult<GetLeadAttributesType>>({ document: getLeadAttributes });

  const handleAddMore = useCallback(() => {
    setLeadFields((prev) => [...prev, initialValue]);
  }, []);

  const handleRemove = useCallback((index: number) => {
    setLeadFields((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSchemaNameChange = useCallback((index: number, value: string) => {
    setLeadFields((prev) => prev.map((item, i) => (i === index ? { ...item, schemaName: value } : item)));
  }, []);

  const handleValueChange = useCallback((index: number, value: string) => {
    setLeadFields((prev) => prev.map((item, i) => (i === index ? { ...item, value } : item)));
  }, []);

  const hanldeSaveAction = useCallback(async () => {
    setIsLoading(true);

    try {
      const parsedNodeData = nodeSchemas.UpdateLeadActionNodeData.parse({
        ...node.data,
        additionalOptions: {
          updateLead: leadFields,
        },
      });

      dispatch(addAdditionalOptions({
        nodeId: node.id,
        additionalOptions: parsedNodeData.additionalOptions,
      }));

      await dispatch(saveActionNode(node.id, parentNodeId, contentMode));
      setMenuOpen(false);
    } catch (error: any) {
      const errorMessage = error instanceof ZodError
        ? error.issues.map((issue) => issue.message).join(', ')
        : error?.data?.message ?? translate(
          `Failed to update #{${TranslationEntity.automation}}`,
          [{ capitalize: true }],
        );
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [contentMode, dispatch, leadFields, node.data, node.id, parentNodeId, setMenuOpen]);

  return (
    <>
      <Card title={translate(
        `Update #{${TranslationEntity.lead}}`,
        [{ capitalize: true }],
      )}
      >
        <List
          footer={(
            <Button
              icon={<PlusOutlined />}
              type="link"
              onClick={handleAddMore}
              className={styles.standaloneButton}
            >
              {translate(
                `Add More #{${TranslationEntity.lead}} Fields`,
                [{ capitalize: true }],
              )}
            </Button>
          )}
          dataSource={leadFields}
          renderItem={(item, index) => (
            <Flex className={styles.itemContainer}>
              <div className={styles.schemaName}>
                <SelectSchemaName
                  value={item.schemaName}
                  onChange={(value) => handleSchemaNameChange(index, value)}
                  options={data?.leadAttributes ?? []}
                  loading={attributesLoading}
                />
              </div>
              <Flex justify="space-between" className={styles.valueContainer}>
                <RenderValueInput
                  value={item.value ?? ''}
                  onChange={(value) => handleValueChange(index, value)}
                  selectedSchemaName={item.schemaName}
                  options={data?.leadAttributes ?? []}
                />
                {index > 0 && (
                  <Button
                    icon={<DeleteOutlined />}
                    type="link"
                    danger
                    onClick={() => handleRemove(index)}
                  >
                    Remove
                  </Button>
                )}
              </Flex>
            </Flex>
          )}
        />
      </Card>

      <Flex justify="flex-end" className={styles.footer}>
        <Button type="primary" loading={isLoading} onClick={hanldeSaveAction}>
          Set Action
        </Button>
      </Flex>
    </>
  );
}
